import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarCheck,
  HeartHandshake,
  ShieldCheck,
  Sparkles,
  Star,
  Stethoscope,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { HYGIENE, SERVICES, TESTIMONIALS } from "@/lib/content";
import { formatAddress, useSettings } from "@/context/SettingsContext";

const HERO =
  "https://images.unsplash.com/photo-1759262151080-e05ba1c6294f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxkZW50aXN0JTIwY2xpbmljJTIwbW9kZXJuJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg2OTAzMjYyfDA&ixlib=rb-4.1.0&q=85";
const CARE =
  "https://images.unsplash.com/photo-1667133295352-ef4c83620e8e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxkZW50aXN0JTIwdHJlYXRpbmclMjBwYXRpZW50fGVufDB8fHx8MTc4NjkwMzI2Mnww&ixlib=rb-4.1.0&q=85";
const HYGIENE_IMG =
  "https://images.unsplash.com/photo-1667133295315-820bb6481730?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxkZW50aXN0JTIwdHJlYXRpbmclMjBwYXRpZW50fGVufDB8fHx8MTc4NjkwMzI2Mnww&ixlib=rb-4.1.0&q=85";

const STATS = [
  { value: "12+", label: "Years in practice" },
  { value: "9,000+", label: "Patients treated" },
  { value: "4.8/5", label: "Average patient rating" },
  { value: "Same-day", label: "Emergency slots" },
];

export default function Home() {
  const { settings } = useSettings();

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-white grain">
        <div className="max-w-7xl mx-auto px-6 md:px-8 pt-16 pb-20 md:pt-24 md:pb-28 grid gap-14 lg:grid-cols-12 items-center">
          <div className="lg:col-span-6 rise">
            <span className="inline-flex items-center gap-2 rounded-full bg-sky-50 text-sky-700 px-4 py-1.5 text-xs tracking-[0.16em] uppercase">
              <Sparkles className="w-3.5 h-3.5" />
              Independent clinic · {settings.city || "Your neighbourhood"}
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900">
              Dentistry that finally
              <span className="block italic text-sky-600">feels gentle.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              At Nidaan Dental Care you see the same dentist every visit. No packages, no upselling — just an
              honest diagnosis, transparent pricing and treatment that respects how nervous teeth can make you.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/book">
                <Button
                  className="rounded-full h-12 px-8 bg-blue-900 text-white hover:bg-blue-950 hover:text-white transition-colors duration-300"
                  data-testid="hero-book-button"
                >
                  Book Appointment
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/about">
                <Button
                  variant="outline"
                  className="rounded-full h-12 px-8 border-slate-300 hover:bg-slate-50"
                  data-testid="hero-services-button"
                >
                  Explore Services
                </Button>
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6" data-testid="trust-stats">
              {STATS.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-2xl text-blue-900">{s.value}</div>
                  <div className="text-xs text-slate-500 mt-1 leading-snug">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 relative rise" style={{ animationDelay: "120ms" }}>
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgb(15,23,42,0.14)]">
              <img
                src={HERO}
                alt="Calm modern dental clinic waiting area"
                className="w-full h-[380px] md:h-[520px] max-h-[80vh] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/45 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 left-6 right-6 md:left-10 md:right-auto md:w-80 bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-sky-500" />
                <p className="text-sm font-medium text-slate-800">Class B autoclave sterilization</p>
              </div>
              <p className="mt-2 text-xs text-slate-500 leading-relaxed">
                Instruments pouched, sealed and cycled before every single patient.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24">
        <div className="max-w-2xl">
          <p className="text-sm tracking-[0.18em] uppercase text-sky-600">What we do</p>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
            Complete dental care under one calm roof
          </h2>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-12">
          {SERVICES.slice(0, 6).map((s, i) => (
            <div
              key={s.key}
              data-testid={`service-card-${s.key}`}
              className={`bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-transform transition-shadow duration-300 ease-out ${
                i % 5 === 0 ? "md:col-span-8" : "md:col-span-4"
              }`}
            >
              <Stethoscope className="w-6 h-6 text-sky-500" />
              <h3 className="mt-5 text-xl font-medium text-slate-900">{s.title}</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Personal care band */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24 grid gap-14 lg:grid-cols-2 items-center">
          <img
            src={CARE}
            alt="Dentist treating a patient gently"
            className="rounded-[2rem] w-full h-[360px] md:h-[460px] max-h-[80vh] object-cover shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
          />
          <div>
            <p className="text-sm tracking-[0.18em] uppercase text-sky-600">One clinic, one dentist</p>
            <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight text-slate-900">
              You will not be passed around
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Nidaan is a single, independent clinic — not a branch of a chain. The dentist who examines you is
              the one who treats you, remembers your history and picks up the phone when something hurts.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                ["Honest treatment plans", "We tell you what can wait, not just what can be billed."],
                ["Transparent pricing", "Written estimates before we begin. No surprises at the desk."],
                ["Comfort first", "Topical numbing, slow anaesthesia and breaks whenever you need one."],
              ].map(([t, d]) => (
                <li key={t} className="flex gap-4">
                  <HeartHandshake className="w-5 h-5 text-sky-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-slate-900 font-medium">{t}</p>
                    <p className="text-sm text-slate-600 mt-1">{d}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Hygiene */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24">
        <div className="grid gap-12 lg:grid-cols-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-sm tracking-[0.18em] uppercase text-sky-600">Safety & hygiene</p>
            <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight text-slate-900">
              Sterilization you can watch happen
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Ask us to show you the autoclave log. We keep it out in the open, because infection control is
              not a marketing line — it is the reason you can relax in the chair.
            </p>
            <img
              src={HYGIENE_IMG}
              alt="Modern dental scanning equipment"
              className="mt-8 rounded-2xl w-full h-56 object-cover"
            />
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6" data-testid="hygiene-grid">
            {HYGIENE.map((h) => (
              <div key={h.title} className="bg-white border border-slate-100 rounded-2xl p-8">
                <ShieldCheck className="w-5 h-5 text-sky-500" />
                <h4 className="mt-4 text-lg font-medium text-slate-900">{h.title}</h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24">
          <p className="text-sm tracking-[0.18em] uppercase text-sky-400">Patient stories</p>
          <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight text-white max-w-2xl">
            What our patients say after the numbness wears off
          </h2>
          <div className="mt-14 grid gap-6 md:grid-cols-3" data-testid="testimonials-grid">
            {TESTIMONIALS.map((t) => (
              <figure key={t.name} className="bg-white/5 border border-white/10 rounded-2xl p-8">
                <div className="flex gap-1 text-sky-400">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <blockquote className="mt-5 text-sm text-slate-200 leading-relaxed">“{t.text}”</blockquote>
                <figcaption className="mt-6">
                  <p className="text-white font-medium">{t.name}</p>
                  <p className="text-xs text-sky-300 mt-1">{t.treatment}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24">
        <div className="bg-white border border-slate-100 rounded-[2rem] p-10 md:p-16 shadow-[0_8px_30px_rgb(0,0,0,0.04)] grid gap-8 md:grid-cols-2 md:items-center">
          <div>
            <CalendarCheck className="w-7 h-7 text-sky-500" />
            <h2 className="mt-5 text-3xl sm:text-4xl tracking-tight text-slate-900">
              Ready when you are
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Pick a day and time that suits you — we will confirm on {settings.phone || "phone"}. Walk-ins are
              welcome too, at {formatAddress(settings) || "our clinic"}.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Link to="/book">
              <Button
                className="rounded-full h-12 px-8 bg-blue-900 text-white hover:bg-blue-950 hover:text-white"
                data-testid="cta-book-button"
              >
                Book Appointment
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="rounded-full h-12 px-8 border-slate-300" data-testid="cta-visit-button">
                Visit Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
